import { useState, useRef, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { io } from "socket.io-client";
import * as Sentry from "@sentry/react";
import { useDeepinfraTTS } from "./tts/useDeepinfraTTS";
import { normaliseChatMessageParts } from "../components/chat-bubble/utils";
import { encodeWAV } from "./audio/encodeWAV";

/**
 * Voice-to-Voice Chat Hook
 * Handles continuous audio capture, STT, LLM processing, and TTS playback
 */
export function useVoiceChat({
  onTranscript,
  onResponse,
  onError,
  activeChatSession,
  chatMessages = [],
  enabled = false, // Add enabled flag for lazy initialization
  silenceThreshold = 0.008,
  maxSilenceMs = 600, // Reduced from 1000ms - shaves the dead-air wait before processing starts
  chunkDurationMs = 1000,
  sampleRate = 16000
} = {}) {
  const { user } = useSelector((state) => state.app.datastate);

  // State
  const [isActive, setIsActive] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState(null);
  const [currentTranscript, setCurrentTranscript] = useState("");

  // Refs
  const socketRef = useRef(null);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const streamRef = useRef(null);
  const sessionIdRef = useRef(null);
  const silenceStartRef = useRef(null);
  const lastSpokeRef = useRef(Date.now());
  const isActiveRef = useRef(false);
  const isSpeakingRef = useRef(false);
  const isProcessingRef = useRef(false);
  const lastProcessedMessageRef = useRef(null);
  const processingTimeoutRef = useRef(null);
  const ttsAbortControllerRef = useRef(null);

  // Voice interruption refs
  const interruptionMonitorRef = useRef(null);
  const interruptionThreshold = useRef(0.05); // Much higher threshold for interruption detection
  const interruptionBufferRef = useRef([]);
  const consecutiveSpeechFrames = useRef(0);
  const requiredSpeechFrames = 8; // Require 8 consecutive frames of speech to trigger interruption
  const interruptionCooldown = useRef(0); // Cooldown to prevent rapid triggering
  const ttsStartTime = useRef(0); // Track when TTS started
  const interruptionGracePeriod = 2000; // 2 seconds grace period after TTS starts

  // Store callback refs to avoid stale closures
  const callbacksRef = useRef({
    onTranscript,
    onResponse,
    onError
  });

  // Latency instrumentation: tracks one round-trip (speech end -> STT sent ->
  // STT received -> LLM first token -> TTS start) to diagnose response time.
  const perfRef = useRef({});
  const logPerfStage = useCallback((stage) => {
    perfRef.current[stage] = performance.now();
  }, []);

  // Update callback refs when they change
  useEffect(() => {
    callbacksRef.current = { onTranscript, onResponse, onError };
  }, [onTranscript, onResponse, onError]);

  // TTS hook - Initialize only when voice chat is enabled
  const ttsHook = useDeepinfraTTS({
    voice: "tara",
    enabled: enabled, // Only enable TTS when voice chat is enabled
    debug: enabled, // Only debug when enabled
  });

  // Audio context initialization ref to ensure proper audio playback
  const audioContextInitializedRef = useRef(false);

  // Update refs when state changes
  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  useEffect(() => {
    isSpeakingRef.current = isSpeaking;
    // Ensure mutually exclusive states
    if (isSpeaking && isListening) {
      setIsListening(false);
    }
  }, [isSpeaking, isListening, isProcessing]);

  useEffect(() => {
    isProcessingRef.current = isProcessing;
  }, [isProcessing]);

  // Helper function to safely resume listening with additional checks
  const safeResumeListening = useCallback((reason = "unknown") => {
    // Additional check: ensure TTS hook isn't currently playing audio
    const isTTSPlaying = ttsHook?.isPlaying || false;

    if (isActiveRef.current && !isSpeakingRef.current && !isProcessingRef.current && !isTTSPlaying) {
      setIsListening(true);
    } else {
      setIsListening(false);
    }
  }, [ttsHook]);

  // Track the session ID when voice chat starts to avoid stopping on session updates
  const voiceChatSessionIdRef = useRef(null);

  // Stop voice chat only when actually switching to a different session
  useEffect(() => {
    const currentSessionId = activeChatSession?._id;

    if (isActive) {
      // If this is the first time or session hasn't been set, record the current session
      if (voiceChatSessionIdRef.current === null || voiceChatSessionIdRef.current === undefined) {
        voiceChatSessionIdRef.current = currentSessionId;
        return;
      }

      // Only stop if we're switching to a genuinely different session
      // Don't stop if we're going from undefined/null to a session (first session creation)
      if (voiceChatSessionIdRef.current !== currentSessionId &&
        voiceChatSessionIdRef.current !== null &&
        voiceChatSessionIdRef.current !== undefined &&
        currentSessionId !== null) {

        // Stop any ongoing TTS
        if (ttsAbortControllerRef.current) {
          ttsAbortControllerRef.current.abort();
          ttsAbortControllerRef.current = null;
        }

        // Immediately stop any playing audio
        if (ttsHook?.stopAudio) {
          ttsHook.stopAudio();
        }

        // Stop voice chat completely
        stopVoiceChat();
        voiceChatSessionIdRef.current = null;
      }
    } else {
      // Reset session tracking when voice chat is not active
      voiceChatSessionIdRef.current = null;
    }
  }, [activeChatSession?._id, isActive]);

  // Refs for streaming TTS
  const streamingTTSRef = useRef({
    currentMessageId: null,
    processedLength: 0,
    isProcessing: false,
    sentences: [],
    currentSentenceIndex: 0
  });

  // REMOVED: Don't extract sentences here - let processSequentialTTS handle it
  // This was causing sentences to be missed and double processing

  // Streaming TTS processor - processes AI responses as they stream
  useEffect(() => {
    if (!isActive) {

      return;
    }
    if (chatMessages.length === 0) {

      return;
    }

    const lastMessage = chatMessages[chatMessages.length - 1];

    // Initialize on first run to prevent playing existing messages
    if (lastProcessedMessageRef.current === null) {

      // CRITICAL FIX: Only mark as processed if there are actually existing messages
      // If this is a fresh session or the last message is from the user, don't mark anything as processed
      const shouldMarkAsProcessed = lastMessage &&
        (lastMessage.user === 'ai' || lastMessage.user === 'AI' || lastMessage.user === 'agent') &&
        chatMessages.length > 1; // More than just one message

      if (shouldMarkAsProcessed) {
        lastProcessedMessageRef.current = lastMessage._id;

      } else {
        lastProcessedMessageRef.current = 'initialized';

      }

      streamingTTSRef.current = {
        currentMessageId: null,
        processedLength: 0,
        isProcessing: false,
        sentences: [],
        currentSentenceIndex: 0
      };

      return;
    }

    // Check if this is an AI message (supports default mode 'AI', and agent/search modes 'agent')
    if (!lastMessage || (lastMessage.user !== 'ai' && lastMessage.user !== 'AI' && lastMessage.user !== 'agent')) {
      return;
    }

    // Extract text content based on message structure
    let messageText = '';
    let isMessageComplete = false;

    if (lastMessage.user === 'agent') {
      // Agent messages store content in message array, extract text from normalized parts
      const isStreamingMessage = lastMessage.message.length === 0 && lastMessage.messageChunks != null;
      const messageParts = isStreamingMessage ? lastMessage.messageChunks : lastMessage.message;

      // For agent messages, check if streaming is complete
      isMessageComplete = !isStreamingMessage && lastMessage.message.length > 0;

      if (messageParts && messageParts.length > 0) {
        const normalisedParts = normaliseChatMessageParts(messageParts);
        const textParts = normalisedParts.filter(part => part.type === "text");
        messageText = textParts.map(part => part.data.text).join('');
      }
    } else {
      // Default AI messages use the text field directly
      messageText = lastMessage.text || '';
      // For regular AI messages, assume complete when text exists
      isMessageComplete = messageText.length > 0;
    }

    const messageId = lastMessage._id;

    // If this is a new message, reset streaming state
    if (streamingTTSRef.current.currentMessageId !== messageId) {
      if (!perfRef.current.llmFirstTokenAt) {
        logPerfStage("llmFirstTokenAt");
      }

      // Mark as processed to prevent full message TTS later
      lastProcessedMessageRef.current = messageId;

      // Reset streaming state for new message
      streamingTTSRef.current = {
        currentMessageId: messageId,
        processedLength: 0,
        isProcessing: false,
        sentences: [],
        currentSentenceIndex: 0,
        proactiveStarted: false
      };

      // Force reset processing state since we got the AI response
      setIsProcessing(false);
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = null;
      }

      if (callbacksRef.current.onResponse) {
        callbacksRef.current.onResponse(messageText);
      }

      // CRITICAL FIX: Ensure audio context is initialized before TTS
      if (!audioContextInitializedRef.current) {

        initializeAudioContext().then(() => {

          startTTSForNewMessage(messageText);
        }).catch(err => {
          Sentry.captureException(err, {
            extra: {
              context: 'useVoiceChat - initializeAudioContext for TTS',
              messageText: messageText?.substring(0, 100)
            }
          });
        });
      } else {
        startTTSForNewMessage(messageText);
      }
    }

    // Helper function to start TTS for new message
    function startTTSForNewMessage(messageText) {
      // CRITICAL OPTIMIZATION: Start proactive TTS generation immediately for first sentence
      if (messageText.length > 30) { // Only if we have enough text

        if (ttsHook?.generateProactiveFirstSentence) {
          ttsHook.generateProactiveFirstSentence(messageText);
        } else {

        }
      }
    }

    // For agent messages, only start TTS when the message is complete
    // For regular AI messages, process incrementally as before
    const shouldProcessTTS = lastMessage.user === 'agent' ? isMessageComplete : true;

    if (!shouldProcessTTS) {

      return;
    }

    // Simplified: Use the proven processSequentialTTS from useDeepinfraTTS.js
    const currentLength = messageText.length;
    const streamingState = streamingTTSRef.current;

    // CRITICAL OPTIMIZATION: Trigger proactive TTS generation as message streams
    if (currentLength > streamingState.processedLength) {
      const newTextLength = currentLength - streamingState.processedLength;

      // If we have significant new text and haven't started proactive generation yet
      if (newTextLength > 20 && currentLength > 50 && !streamingState.proactiveStarted) {

        if (ttsHook?.generateProactiveFirstSentence) {
          ttsHook.generateProactiveFirstSentence(messageText);
          streamingState.proactiveStarted = true;
        }
      }
    }

    // Only process if we have new text and we're not already processing
    if (currentLength > streamingState.processedLength && !streamingState.isProcessing) {

      // Mark as processed immediately to prevent reprocessing
      streamingState.processedLength = currentLength;

      // Start TTS if not already speaking
      if (!isSpeakingRef.current) {

        startStreamingTTS(messageText);
      } else {

      }
    }
  }, [chatMessages, isActive]);

  // Voice interruption detection
  const startInterruptionMonitoring = useCallback(() => {
    if (!streamRef.current || !audioContextRef.current || interruptionMonitorRef.current) {
      return;
    }

    try {
      const source = audioContextRef.current.createMediaStreamSource(streamRef.current);
      const processor = audioContextRef.current.createScriptProcessor(1024, 1, 1);

      interruptionMonitorRef.current = { source, processor };

      source.connect(processor);
      processor.connect(audioContextRef.current.destination);

      processor.onaudioprocess = (e) => {
        if (!isSpeakingRef.current || !isActiveRef.current) {
          consecutiveSpeechFrames.current = 0;
          return;
        }

        // Grace period - don't allow interruption for first few seconds of TTS
        const timeSinceTTSStart = Date.now() - ttsStartTime.current;
        if (timeSinceTTSStart < interruptionGracePeriod) {
          consecutiveSpeechFrames.current = 0;
          return;
        }

        // Cooldown check - prevent rapid triggering
        if (Date.now() - interruptionCooldown.current < 1000) {
          return;
        }

        const input = e.inputBuffer.getChannelData(0);

        // Calculate RMS for voice activity detection
        let sum = 0;
        for (let i = 0; i < input.length; i++) {
          sum += input[i] * input[i];
        }
        const rms = Math.sqrt(sum / input.length);

        // Much more conservative interruption detection
        // Only trigger on very clear, loud speech that's significantly above background
        if (rms > interruptionThreshold.current) {
          consecutiveSpeechFrames.current++;

          // Log every few frames to see what's happening (less frequent logging)
          if (consecutiveSpeechFrames.current % 6 === 0) {
            
          }

          if (consecutiveSpeechFrames.current >= requiredSpeechFrames) {
            
            interruptionCooldown.current = Date.now();
            handleVoiceInterruption();
          }
        } else {
          // Reset counter if audio drops below threshold
          if (consecutiveSpeechFrames.current > 0) {
            consecutiveSpeechFrames.current = Math.max(0, consecutiveSpeechFrames.current - 1);
          }
        }
      };

    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: 'useVoiceChat - startInterruptionMonitoring',
          hasAudioContext: !!audioContextRef.current,
          hasStream: !!streamRef.current,
          isActive: isActiveRef.current,
          isSpeaking: isSpeakingRef.current
        }
      });
      Sentry.captureException(error, {
        extra: {
          context: 'useVoiceChat - startInterruptionMonitoring',
          hasAudioContext: !!audioContextRef.current,
          hasStream: !!streamRef.current
        }
      });
    }
  }, []);

  const stopInterruptionMonitoring = useCallback(() => {
    if (interruptionMonitorRef.current) {

      try {
        interruptionMonitorRef.current.processor.disconnect();
        interruptionMonitorRef.current.source.disconnect();
      } catch (error) {
        Sentry.captureException(error, {
          extra: { 
            context: 'stopInterruptionMonitoring - disconnecting audio processors',
            hasProcessor: !!interruptionMonitorRef.current?.processor,
            hasSource: !!interruptionMonitorRef.current?.source
          }
        });

      }

      interruptionMonitorRef.current = null;
      consecutiveSpeechFrames.current = 0;
      interruptionCooldown.current = 0;
      ttsStartTime.current = 0;
    }
  }, []);

  const handleVoiceInterruption = useCallback(() => {

    // Immediately abort all TTS generation and playback
    if (ttsAbortControllerRef.current) {

      ttsAbortControllerRef.current.abort();
      ttsAbortControllerRef.current = null;
    }

    // Stop all audio playback immediately
    if (ttsHook?.stopAudio) {

      ttsHook.stopAudio();
    }

    if (ttsHook?.cancelGeneration) {

      ttsHook.cancelGeneration();
    }

    // Stop streaming TTS processing
    if (streamingTTSRef.current) {

      streamingTTSRef.current.isProcessing = false;
    }

    // Stop interruption monitoring
    stopInterruptionMonitoring();

    // Reset states immediately
    setIsSpeaking(false);
    setIsProcessing(false);

    // Immediately start listening for user input
    setTimeout(() => {
      if (isActiveRef.current) {

        setIsListening(true);
      }
    }, 100);

    consecutiveSpeechFrames.current = 0;
  }, [ttsHook, stopInterruptionMonitoring]);

  // Initialize audio context for first-time use
  const initializeAudioContext = useCallback(async () => {
    if (audioContextInitializedRef.current) {
      return;
    }

    try {

      // Create a temporary audio context to test and initialize audio
      const tempAudioContext = new (window.AudioContext || window.webkitAudioContext)();

      if (tempAudioContext.state === 'suspended') {

        await tempAudioContext.resume();
      }

      // Create a silent audio buffer to prime the audio system
      const buffer = tempAudioContext.createBuffer(1, 1, 22050);
      const source = tempAudioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(tempAudioContext.destination);

      // Play silent audio to initialize the audio system
      source.start();

      // Clean up temporary context
      setTimeout(() => {
        tempAudioContext.close();
      }, 100);

      audioContextInitializedRef.current = true;

    } catch (error) {
      Sentry.captureException(error, {
        extra: { 
          context: 'initializeAudioContext - audio system initialization',
          audioContextSupported: !!(window.AudioContext || window.webkitAudioContext),
          audioContextInitialized: audioContextInitializedRef.current
        }
      });

      // Don't block execution, just log the warning
    }
  }, []);

  // Enhanced streaming TTS with proper state synchronization
  const startStreamingTTS = useCallback(async (messageText) => {
    const streamingState = streamingTTSRef.current;

    if (streamingState.isProcessing || !messageText?.trim()) {
      return;
    }

    // CRITICAL FIX: Initialize audio context on first use
    if (!audioContextInitializedRef.current) {

      await initializeAudioContext();
    }

    streamingState.isProcessing = true;
    setIsSpeaking(true);
    setIsListening(false); // 🔧 Ensure listening is stopped when TTS starts

    if (!perfRef.current.ttsStartAt) {
      logPerfStage("ttsStartAt");
      const p = perfRef.current;
      if (p.speechEndAt) {
        console.info(
          "[voice-chat latency] " +
          `silence->STT sent: ${(p.sttSentAt - p.speechEndAt).toFixed(0)}ms | ` +
          `STT round-trip: ${(p.sttReceivedAt - p.sttSentAt).toFixed(0)}ms | ` +
          `STT->LLM first token: ${(p.llmFirstTokenAt - p.sttReceivedAt).toFixed(0)}ms | ` +
          `LLM first token->TTS start: ${(p.ttsStartAt - p.llmFirstTokenAt).toFixed(0)}ms | ` +
          `TOTAL (silence->speaking): ${(p.ttsStartAt - p.speechEndAt).toFixed(0)}ms`
        );
      }
    }

    // Record TTS start time and start monitoring for voice interruptions
    ttsStartTime.current = Date.now();
    startInterruptionMonitoring();

    // Create abort controller for this TTS session
    const abortController = new AbortController();
    ttsAbortControllerRef.current = abortController;

    // Track if any audio is currently playing
    let isAnyAudioPlaying = false;
    let lastAudioPromise = Promise.resolve();

    try {
      // Use the proven processSequentialTTS method - it handles sentence splitting internally
      await ttsHook.processSequentialTTS(
        messageText,
        async (audioBlob) => {
          // Check if we should abort before playing
          if (abortController.signal.aborted || !streamingState.isProcessing) {

            return;
          }

          // 🔧 CRITICAL FIX: Track audio playback state properly
          try {

            isAnyAudioPlaying = true;

            // Use the sequential audio method for voice chat to prevent overlap
            const audioPromise = ttsHook.playAudioSequential(audioBlob, { autoPlay: true, volume: 1 });
            lastAudioPromise = audioPromise;

            // Wait for audio to complete or be aborted
            await Promise.race([
              audioPromise,
              new Promise((_, reject) => {
                // Handle abort during playback
                const abortHandler = () => {

                  reject(new Error('Audio playback aborted'));
                };

                abortController.signal.addEventListener('abort', abortHandler);

                // Check for interruption during playback
                const interruptionCheck = setInterval(() => {
                  if (!streamingState.isProcessing || !isActiveRef.current || abortController.signal.aborted) {

                    clearInterval(interruptionCheck);
                    abortController.signal.removeEventListener('abort', abortHandler);
                    reject(new Error('Audio playback interrupted'));
                  }
                }, 100);

                // Cleanup interval when audio completes
                audioPromise.finally(() => {
                  clearInterval(interruptionCheck);
                  abortController.signal.removeEventListener('abort', abortHandler);
                });
              })
            ]);

          } catch (audioError) {
            if (audioError.message.includes('abort') || audioError.message.includes('interrupt')) {

              return; // Exit early on abort/interrupt
            } else {
              Sentry.captureException(audioError, {
                extra: { 
                  context: 'startStreamingTTS - audio playback error',
                  messageText: messageText?.substring(0, 100),
                  isAnyAudioPlaying,
                  isProcessing: streamingState.isProcessing,
                  isActive: isActiveRef.current
                }
              });
              // Don't throw - continue with next sentence
            }
          } finally {
            isAnyAudioPlaying = false;
          }
        },
        abortController.signal
      );

      // 🔧 CRITICAL FIX: Wait for the last audio to finish before changing state
      if (lastAudioPromise && !abortController.signal.aborted) {

        try {
          await lastAudioPromise;

        } catch (error) {
          if (!error.message.includes('abort') && !error.message.includes('interrupt')) {
            Sentry.captureException(error, {
              extra: { 
                context: 'startStreamingTTS - final audio completion error',
                messageText: messageText?.substring(0, 100),
                aborted: abortController.signal.aborted
              }
            });
          }
          
        }
      }

    } catch (error) {
      if (error.name !== 'AbortError') {
        Sentry.captureException(error, {
          extra: { 
            context: 'startStreamingTTS - processSequentialTTS error',
            messageText: messageText?.substring(0, 100),
            errorName: error.name,
            isProcessing: streamingState.isProcessing,
            aborted: ttsAbortControllerRef.current?.signal?.aborted
          }
        });
      }
    } finally {
      // Clean abort controller
      if (ttsAbortControllerRef.current === abortController) {
        ttsAbortControllerRef.current = null;
      }

      streamingState.isProcessing = false;
      
      // 🔧 CRITICAL FIX: Only set speaking to false after ensuring no audio is playing

      setIsSpeaking(false);

      // Stop interruption monitoring
      stopInterruptionMonitoring();

      // 🔧 ENHANCED: Wait longer and double-check state before resuming listening
      if (isActiveRef.current && !abortController.signal.aborted) {
        setTimeout(() => {
          // Double-check that we're not speaking before resuming
          if (!isSpeakingRef.current && isActiveRef.current) {

            safeResumeListening("TTS fully completed");
          } else {

          }
        }, 1000); // Increased delay to ensure audio completion
      }
    }
  }, [ttsHook, safeResumeListening, startInterruptionMonitoring, stopInterruptionMonitoring]);

  // Initialize socket connection for STT only - wait for chat provider to be initialized
  useEffect(() => {
    // Only initialize when enabled and user is available
    if (!enabled || !user?._id) {
      if (!enabled) {

      } else {

      }
      return;
    }

    // Clean up existing socket
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    try {
      socketRef.current = io(import.meta.env.VITE_SOCKET_URL, {
        query: { user: user._id, userToken: `Bearer ${user.userToken}` },
        transports: ['websocket', 'polling'],
        timeout: 20000,
        forceNew: true // Force new connection to prevent issues
      });

      const socket = socketRef.current;

      socket.on("connect", () => {

      });

      socket.on("connect_error", (error) => {
        Sentry.captureException(error, {
          extra: {
            context: 'useVoiceChat - STT Socket connection error',
            userId: user?._id,
            socketUrl: import.meta.env.VITE_SOCKET_URL
          }
        });
        const err = new Error(`STT Socket connection failed: ${error.message}`);
        setError(err);
        if (callbacksRef.current.onError) {
          callbacksRef.current.onError(err);
        }
      });

      socket.on("disconnect", (reason) => {

        // Only set error if it's an unexpected disconnection and voice chat is still active
        if (reason !== "io client disconnect" && isActiveRef.current) {
          const err = new Error(`STT Socket disconnected: ${reason}`);
          setError(err);
          if (callbacksRef.current.onError) {
            callbacksRef.current.onError(err);
          }
        }
      });

      // Handle transcript events
      socket.on("TRANSCRIPT_PARTIAL", (data) => {
        // 🔧 Enhanced check: Ensure voice chat is still active
        if (!data?.text || !isActiveRef.current) {
          if (!isActiveRef.current) {

          }
          return;
        }

        // 🔧 Additional check: Ensure this transcript is from the current session
        if (data.sessionId && sessionIdRef.current && data.sessionId !== sessionIdRef.current) {

          return;
        }

        setCurrentTranscript(data.text);
      });

      socket.on("TRANSCRIPT_FINAL", (data) => {
        // 🔧 Enhanced check: Ensure voice chat is still active and session is valid
        if (!data?.text || !isActiveRef.current) {
          if (!isActiveRef.current && data?.text) {

          }
          return;
        }

        // 🔧 Additional check: Ensure this transcript is from the current session
        if (data.sessionId && sessionIdRef.current && data.sessionId !== sessionIdRef.current) {

          return;
        }

        setCurrentTranscript("");

        logPerfStage("sttReceivedAt");
        // Process the final transcript
        processTranscriptWithLLM(data.text);
      });

    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: 'useVoiceChat - Failed to create STT socket',
          userId: user?._id
        }
      });
      const err = new Error(`Failed to initialize STT socket: ${error.message}`);
      setError(err);
      if (callbacksRef.current.onError) {
        callbacksRef.current.onError(err);
      }
    }

    return () => {

      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [enabled, user?._id, user?.userToken]);

  // Process transcript with LLM using regular chat system
  const processTranscriptWithLLM = useCallback(async (transcript) => {
    // 🔧 CRITICAL: Don't process transcript if voice chat is not active
    if (!isActiveRef.current) {
      
      setIsProcessing(false);
      return;
    }

    // CRITICAL: Don't process transcript if AI is speaking
    if (isSpeakingRef.current) {

      setIsProcessing(false);
      return;
    }

    try {

      setIsProcessing(true);
      setIsListening(false);

      // Set processing timeout
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      processingTimeoutRef.current = setTimeout(() => {

        setIsProcessing(false);
        safeResumeListening("processing timeout");
      }, 15000);

      if (callbacksRef.current.onTranscript) {
        callbacksRef.current.onTranscript(transcript);
      }

    } catch (err) {
      Sentry.captureException(err, {
        extra: {
          context: 'useVoiceChat - processTranscriptWithLLM',
          isActive: isActiveRef.current,
          isSpeaking: isSpeakingRef.current
        }
      });
      setIsProcessing(false);
      if (isActiveRef.current) {
        safeResumeListening("transcript processing error");
      }
      throw err;
    }
  }, [safeResumeListening]);

  // Enhanced silence detection with better accuracy
  const isSilent = useCallback((buffer) => {
    // Calculate RMS (Root Mean Square) for overall energy
    let sum = 0;
    for (let i = 0; i < buffer.length; i++) {
      sum += buffer[i] * buffer[i];
    }
    const rms = Math.sqrt(sum / buffer.length);

    // Calculate zero-crossing rate for additional voice activity detection
    let zeroCrossings = 0;
    for (let i = 1; i < buffer.length; i++) {
      if ((buffer[i] >= 0) !== (buffer[i - 1] >= 0)) {
        zeroCrossings++;
      }
    }
    const zeroCrossingRate = zeroCrossings / buffer.length;

    // Enhanced silence detection: combine RMS and zero-crossing rate
    // Voice typically has higher zero-crossing rate than pure noise
    const isRMSSilent = rms < silenceThreshold;
    const hasVoiceCharacteristics = zeroCrossingRate > 0.02; // Voice typically > 0.02

    // Consider it silent if RMS is low AND it doesn't have voice characteristics
    const silent = isRMSSilent || (!hasVoiceCharacteristics && rms < silenceThreshold * 2);

    if (!silent && Date.now() % 1000 < 100) {
      
    }

    return silent;
  }, [silenceThreshold]);

  // Continuous audio buffer for capturing user speech even during AI speaking
  const continuousAudioBufferRef = useRef([]);
  const continuousRecordingRef = useRef(false);
  const userSpeechDetectedRef = useRef(false);

  // Start continuous audio monitoring (always recording in background)
  const startContinuousAudioMonitoring = useCallback(async () => {
    if (continuousRecordingRef.current || !streamRef.current || !audioContextRef.current) {
      return;
    }

    continuousRecordingRef.current = true;
    continuousAudioBufferRef.current = [];
    userSpeechDetectedRef.current = false;

    try {
      const source = audioContextRef.current.createMediaStreamSource(streamRef.current);
      const processor = audioContextRef.current.createScriptProcessor(1024, 1, 1);

      interruptionMonitorRef.current = { source, processor };

      source.connect(processor);
      processor.connect(audioContextRef.current.destination);

      processor.onaudioprocess = (e) => {
        if (!continuousRecordingRef.current || !isActiveRef.current) {
          return;
        }

        const input = e.inputBuffer.getChannelData(0);

        // Always buffer audio data
        continuousAudioBufferRef.current.push(new Float32Array(input));

        // Keep buffer size manageable (last 10 seconds)
        const maxBufferLength = Math.floor(10 * (audioContextRef.current?.sampleRate || sampleRate) / 1024);
        if (continuousAudioBufferRef.current.length > maxBufferLength) {
          continuousAudioBufferRef.current.shift();
        }

        // Detect user speech
        const silent = isSilent(input);

        if (!silent) {
          userSpeechDetectedRef.current = true;
          lastSpokeRef.current = Date.now();

          // If AI is speaking and user starts talking, handle interruption
          if (isSpeakingRef.current) {
            consecutiveSpeechFrames.current++;

            // Grace period - don't allow interruption for first few seconds of TTS
            const timeSinceTTSStart = Date.now() - ttsStartTime.current;
            if (timeSinceTTSStart > interruptionGracePeriod &&
              consecutiveSpeechFrames.current >= requiredSpeechFrames &&
              Date.now() - interruptionCooldown.current > 1000) {

              interruptionCooldown.current = Date.now();
              handleVoiceInterruption();
            }
          }
        } else {
          consecutiveSpeechFrames.current = Math.max(0, consecutiveSpeechFrames.current - 1);
        }

        // Process user speech when AI is not speaking
        if (!isSpeakingRef.current && !isProcessingRef.current && isListening) {
          // Regular STT processing logic here
          if (!silenceStartRef.current && !silent) {
            silenceStartRef.current = null;
          } else if (silent && !silenceStartRef.current) {
            silenceStartRef.current = Date.now();
          }

          // Check for end of user speech
          if (silenceStartRef.current &&
            Date.now() - silenceStartRef.current > maxSilenceMs &&
            userSpeechDetectedRef.current) {

            processUserSpeech();
          }
        }
      };

    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: 'useVoiceChat - startContinuousAudioMonitoring',
          hasStream: !!streamRef.current,
          hasAudioContext: !!audioContextRef.current
        }
      });
      continuousRecordingRef.current = false;
    }
  }, [isSilent, handleVoiceInterruption, maxSilenceMs, isListening]);

  // Process accumulated user speech
  const processUserSpeech = useCallback(async () => {
    if (!continuousAudioBufferRef.current.length || isProcessingRef.current) {
      return;
    }

    try {
      perfRef.current = { speechEndAt: performance.now() };
      setIsProcessing(true);
      setIsListening(false);
      userSpeechDetectedRef.current = false;
      silenceStartRef.current = null;

      // Combine all buffered audio
      const totalLength = continuousAudioBufferRef.current.reduce((acc, arr) => acc + arr.length, 0);
      const combinedAudio = new Float32Array(totalLength);
      let offset = 0;

      for (const chunk of continuousAudioBufferRef.current) {
        combinedAudio.set(chunk, offset);
        offset += chunk.length;
      }

      // Clear the buffer
      continuousAudioBufferRef.current = [];

      // Convert to WAV and send for transcription
      const wavBlob = encodeWAV(combinedAudio, sampleRate);

      if (wavBlob.size > 0) {
        await transcribeAudio(wavBlob);
      }

    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: 'useVoiceChat - processUserSpeech',
          bufferLength: continuousAudioBufferRef.current?.length
        }
      });
      setIsProcessing(false);
      if (isActiveRef.current) {
        safeResumeListening("user speech processing error");
      }
    }
  }, [safeResumeListening, sampleRate]);

  // Stop continuous audio monitoring
  const stopContinuousAudioMonitoring = useCallback(() => {
    if (!continuousRecordingRef.current) {
      return;
    }

    continuousRecordingRef.current = false;
    continuousAudioBufferRef.current = [];
    userSpeechDetectedRef.current = false;

    if (interruptionMonitorRef.current?.continuousSource) {
      try {
        interruptionMonitorRef.current.continuousProcessor.disconnect();
        interruptionMonitorRef.current.continuousSource.disconnect();
      } catch (error) {

      }
      interruptionMonitorRef.current.continuousSource = null;
      interruptionMonitorRef.current.continuousProcessor = null;
    }
  }, []);

  // Transcribe audio using socket
  const transcribeAudio = useCallback(async (wavBlob) => {
    if (!socketRef.current || !socketRef.current.connected) {

      return;
    }

    if (!wavBlob || wavBlob.size === 0) {

      return;
    }

    try {

      const arrayBuffer = await wavBlob.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      // Create session ID if not exists
      if (!sessionIdRef.current) {
        sessionIdRef.current = `voice_chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      }

      logPerfStage("sttSentAt");
      socketRef.current.emit("audio-chunk", {
        chunk: Array.from(uint8Array),
        language: "en",
        userId: user?._id,
        sessionId: sessionIdRef.current,
        timestamp: Date.now()
      });

    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: 'useVoiceChat - transcribeAudio',
          userId: user?._id,
          socketConnected: socketRef.current?.connected
        }
      });
      throw new Error(`Failed to transcribe audio: ${error.message}`);
    }
  }, [user?._id, logPerfStage]);

  // Start voice chat session
  const startVoiceChat = useCallback(async () => {

    try {
      setError(null);

      if (!user?._id) {
        throw new Error("User not authenticated - please log in");
      }

      if (!import.meta.env.VITE_SOCKET_URL) {
        throw new Error("VITE_SOCKET_URL environment variable is not configured");
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("Browser does not support microphone access");
      }

      if (!socketRef.current) {
        throw new Error("Voice chat not ready - please wait for the chat to fully load and try again");
      }

      if (!socketRef.current.connected) {

        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error("Socket connection timeout"));
          }, 10000);

          if (socketRef.current.connected) {
            clearTimeout(timeout);
            resolve();
          } else {
            socketRef.current.once('connect', () => {
              clearTimeout(timeout);
              resolve();
            });
            socketRef.current.once('connect_error', (error) => {
              clearTimeout(timeout);
              reject(new Error(`Socket connection failed: ${error.message}`));
            });
          }
        });
      }

      setIsActive(true);

      // CRITICAL FIX: Initialize audio context early to prevent first-time playback issues

      await initializeAudioContext();

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: sampleRate,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      streamRef.current = stream;

      await startAudioProcessing(stream);

      // Start continuous audio monitoring for always-on recording
      await startContinuousAudioMonitoring();

    } catch (err) {
      Sentry.captureException(err, {
        extra: {
          context: 'useVoiceChat - startVoiceChat',
          userId: user?._id,
          hasSocket: !!socketRef.current,
          socketConnected: socketRef.current?.connected
        }
      });
      setError(err);
      setIsActive(false);

      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }

      if (callbacksRef.current.onError) {
        callbacksRef.current.onError(err);
      }
      throw err;
    }
  }, [user, sampleRate, startContinuousAudioMonitoring]);

  // Stop voice chat session
  const stopVoiceChat = useCallback(() => {

    // 🔧 CRITICAL: Set inactive state first to prevent any new processing
    setIsActive(false);
    setIsListening(false);
    setIsSpeaking(false);
    setIsProcessing(false); // 🔧 Also stop any ongoing processing
    setCurrentTranscript("");
    setError(null);

    lastProcessedMessageRef.current = null;

    // 🔧 Cancel any ongoing STT processing
    if (socketRef.current && sessionIdRef.current) {

      socketRef.current.emit("cancel-stt-session", { 
        userId: user?._id, 
        sessionId: sessionIdRef.current 
      });
    }

    // 🔧 Clear session ID to prevent processing of late-arriving transcripts
    sessionIdRef.current = null;

    // CRITICAL FIX: Don't reset audio context - keep it initialized for better performance
    // audioContextInitializedRef.current = false; // Removed - causes first session TTS issues

    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
      processingTimeoutRef.current = null;
    }

    if (ttsAbortControllerRef.current) {
      ttsAbortControllerRef.current.abort();
      ttsAbortControllerRef.current = null;
    }

    // Stop interruption monitoring and continuous audio monitoring
    stopInterruptionMonitoring();
    stopContinuousAudioMonitoring();

    if (processorRef.current) {
      try {
        processorRef.current.disconnect();
        processorRef.current = null;
      } catch (err) {

      }
    }

    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      try {
        audioContextRef.current.close();
        audioContextRef.current = null;
      } catch (err) {

      }
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (ttsHook?.stopAudio) {
      ttsHook.stopAudio();
    }
    if (ttsHook?.cancelGeneration) {
      ttsHook.cancelGeneration();
    }

  }, [ttsHook, stopInterruptionMonitoring, stopContinuousAudioMonitoring]);

  // Start audio processing
  const startAudioProcessing = useCallback(async (stream) => {

    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioContextClass({
        sampleRate: sampleRate
      });
      audioContextRef.current = audioCtx;

      if (audioCtx.state === 'suspended') {

        await audioCtx.resume();
      }

      const source = audioCtx.createMediaStreamSource(stream);

      // Use modern AudioWorklet if available, fallback to ScriptProcessor
      let processor;
      if (audioCtx.audioWorklet) {
        // Modern approach - would need separate worklet file
        processor = audioCtx.createScriptProcessor(4096, 1, 1);
      } else {
        processor = audioCtx.createScriptProcessor(4096, 1, 1);
      }

      processorRef.current = processor;

      source.connect(processor);
      processor.connect(audioCtx.destination);

      let chunkPCM = [];
      let chunkStart = Date.now();
      let hasDetectedSpeech = false;

      setIsListening(true);

      processor.onaudioprocess = (e) => {
        if (!isActiveRef.current || isSpeakingRef.current || isProcessingRef.current) {
          if (chunkPCM.length > 0) {
            chunkPCM = [];
            hasDetectedSpeech = false;
            silenceStartRef.current = null;
          }
          return;
        }

        const input = e.inputBuffer.getChannelData(0);
        const silent = isSilent(input);

        if (!silent) {
          if (Date.now() % 500 < 100) {

          }
          hasDetectedSpeech = true;
          chunkPCM.push(new Float32Array(input));
          silenceStartRef.current = null;
          lastSpokeRef.current = Date.now();
        } else {
          if (hasDetectedSpeech) {
            chunkPCM.push(new Float32Array(input));

            if (!silenceStartRef.current) {
              silenceStartRef.current = Date.now();

            }

            const silenceDuration = Date.now() - silenceStartRef.current;

            const hasEnoughSilence = silenceDuration >= maxSilenceMs;
            const hasEnoughAudio = chunkPCM.length >= 10; // Reduced from 15 to 10 for faster response
            const hasShortSilence = silenceDuration >= 700 && chunkPCM.length >= 20; // Reduced from 1000ms to 700ms and 25 to 20 chunks
            const hasLongAudio = chunkPCM.length > 50 && silenceDuration >= 600; // Reduced from 60 chunks and 800ms

            const shouldProcess = hasEnoughAudio && (hasEnoughSilence || hasShortSilence || hasLongAudio);

            if (shouldProcess && chunkPCM.length > 0) {
              if (isSpeakingRef.current) {

                chunkPCM = [];
                hasDetectedSpeech = false;
                silenceStartRef.current = null;
                return;
              }

              setIsListening(false);
              setIsProcessing(true);

              if (processingTimeoutRef.current) {
                clearTimeout(processingTimeoutRef.current);
              }
              processingTimeoutRef.current = setTimeout(() => {

                setIsProcessing(false);
                safeResumeListening("processing timeout");
              }, 15000);

              processCompleteAudio(chunkPCM);

              chunkPCM = [];
              chunkStart = Date.now();
              silenceStartRef.current = null;
              hasDetectedSpeech = false;
            }
          }
        }

        if (!hasDetectedSpeech && Date.now() - chunkStart > 10000) {
          chunkPCM = [];
          chunkStart = Date.now();
        }

        if (hasDetectedSpeech && chunkPCM.length > 80) { // Reduced from 100 to 80

          setIsListening(false);
          setIsProcessing(true);
          processCompleteAudio(chunkPCM);
          chunkPCM = [];
          chunkStart = Date.now();
          silenceStartRef.current = null;
          hasDetectedSpeech = false;
        }
      };

    } catch (err) {
      Sentry.captureException(err, {
        extra: {
          context: 'useVoiceChat - startAudioProcessing',
          sampleRate,
          hasStream: !!stream
        }
      });
      throw err;
    }
  }, [sampleRate, isSilent, safeResumeListening, maxSilenceMs]);

  // Process complete audio after silence detection
  const processCompleteAudio = useCallback(async (chunkPCM) => {
    if (isSpeakingRef.current) {

      setIsProcessing(false);
      return;
    }

    if (isProcessingRef.current && chunkPCM.length === 0) {

      return;
    }

    if (chunkPCM.length === 0) {

      setIsProcessing(false);
      if (isActiveRef.current && !isSpeakingRef.current) {
        safeResumeListening("no audio to process");
      }
      return;
    }

    try {

      const totalLength = chunkPCM.reduce((acc, arr) => acc + arr.length, 0);
      const flatPCM = new Float32Array(totalLength);
      let offset = 0;

      for (const arr of chunkPCM) {
        flatPCM.set(arr, offset);
        offset += arr.length;
      }

      let sum = 0;
      for (let i = 0; i < flatPCM.length; i++) {
        sum += flatPCM[i] * flatPCM[i];
      }
      const audioRMS = Math.sqrt(sum / flatPCM.length);

      if (audioRMS < 0.008) {

        setIsProcessing(false);
        if (isActiveRef.current) {
          safeResumeListening("low audio RMS");
        }
        return;
      }

      const wavBlob = encodeWAV(flatPCM, sampleRate);

      const transcript = await convertAudioToText(wavBlob);

      if (!transcript || transcript.trim().length === 0 || transcript.trim() === '-') {

        setIsProcessing(false);
        if (isActiveRef.current) {
          safeResumeListening("no meaningful transcript");
        }
        return;
      }

      setCurrentTranscript(transcript);

      await processTranscriptWithLLM(transcript);

    } catch (err) {
      Sentry.captureException(err, {
        extra: {
          context: 'useVoiceChat - processCompleteAudio',
          chunkCount: chunkPCM?.length,
          isSTTTimeout: err?.message?.includes("STT timeout")
        }
      });
      const error = err instanceof Error ? err : new Error(String(err));

      // CRITICAL FIX: Don't show error popups for STT timeouts - they're common and not critical
      const isSTTTimeout = error.message.includes("STT timeout") || error.message.includes("timeout");

      if (isSTTTimeout) {

        // Don't set error or call onError for STT timeouts
      } else {
        setError(error);
        if (callbacksRef.current.onError) {
          callbacksRef.current.onError(error);
        }
      }

      setIsProcessing(false);
      if (isActiveRef.current) {
        safeResumeListening("audio processing error");
      }
    }
  }, [sampleRate, safeResumeListening, processTranscriptWithLLM]);

  // Convert audio to text using STT service
  const convertAudioToText = useCallback(async (audioBlob) => {
    return new Promise((resolve, reject) => {
      if (!socketRef.current?.connected) {
        reject(new Error("Socket not connected"));
        return;
      }

      const handleTranscriptFinal = (data) => {

        cleanup();
        resolve(data.text);
      };

      const handleError = (data) => {
        Sentry.captureException(new Error(data.message), {
          extra: {
            context: 'useVoiceChat - STT error from socket',
            userId: user?._id
          }
        });
        cleanup();
        reject(new Error(data.message));
      };

      const cleanup = () => {
        if (socketRef.current) {
          socketRef.current.off("TRANSCRIPT_FINAL", handleTranscriptFinal);
          socketRef.current.off("voice-chat-error", handleError);
        }
      };

      socketRef.current.on("TRANSCRIPT_FINAL", handleTranscriptFinal);
      socketRef.current.on("voice-chat-error", handleError);

      audioBlob.arrayBuffer().then(arrayBuffer => {
        const uint8Array = new Uint8Array(arrayBuffer);
        socketRef.current.emit("audio-end", {
          userId: user?._id,
          combinedAudio: uint8Array
        });
      }).catch(reject);

      // CRITICAL FIX: Increased timeout and better error handling
      setTimeout(() => {

        cleanup();
        reject(new Error("STT timeout"));
      }, 15000); // Increased from 10s to 15s
    });
  }, [user?._id]);

  // Manual processing trigger
  const forceProcessAudio = useCallback(() => {
    if (!isActive || isProcessing || isSpeaking) {

      return;
    }

    setIsListening(false);
    setIsProcessing(true);
  }, [isActive, isProcessing, isSpeaking]);

  // Cleanup when component unmounts
  useEffect(() => {
    return () => {

      if (ttsAbortControllerRef.current) {
        ttsAbortControllerRef.current.abort();
        ttsAbortControllerRef.current = null;
      }

      // Stop interruption monitoring
      stopInterruptionMonitoring();

      if (streamRef.current) {

        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }

      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {

        audioContextRef.current.close();
        audioContextRef.current = null;
      }

      if (socketRef.current) {

        socketRef.current.disconnect();
        socketRef.current = null;
      }

      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = null;
      }
    };
  }, [stopInterruptionMonitoring]);

  // Stop audio when voice chat becomes inactive
  useEffect(() => {
    if (!isActive && (isSpeaking || isProcessing)) {

      if (ttsAbortControllerRef.current) {
        ttsAbortControllerRef.current.abort();
        ttsAbortControllerRef.current = null;
      }

      // Stop interruption monitoring
      stopInterruptionMonitoring();

      if (ttsHook?.stopAudio) {
        ttsHook.stopAudio();
      }

      setIsSpeaking(false);
      setIsProcessing(false);
    }
  }, [isActive, isSpeaking, isProcessing, ttsHook, stopInterruptionMonitoring]);

  return {
    // State
    isActive,
    isListening,
    isProcessing,
    isSpeaking,
    error,
    currentTranscript,

    // Actions
    startVoiceChat,
    stopVoiceChat,
    forceProcessAudio,
    interruptAI: handleVoiceInterruption
  };
}